/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import LoadingPage from "components/common_components/LoadingPage";
import { useDispatch } from "react-redux";
import { notifyError, notifyInfo } from "redux/actions/Notifications";
import clsx from "clsx";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import { TextField } from "@material-ui/core";

import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import FormsContext from "../FormsContext";
import { hasElements } from "utils/arrays";
import { ConfirmDialog } from "components/common_components/Dialogs";
import Status from "components/common_components/Status";
import SearchByToggle, { SearchBy } from "./SearchByToggle";

const headCells = [
    { index: 1, id: "firstName", disablePadding: false, label: "First Name" },
    { index: 2, id: "middleName", disablePadding: false, label: "Middle Name" },
    { index: 3, id: "lastName", disablePadding: false, label: "Last Name" },
    { index: 4, id: "email", disablePadding: true, label: "Email" },
    {
        index: 5,
        id: "enabled",
        disablePadding: true,
        label: "Status",
        type: "bool",
        render: (_, { value, data }) => {
            return <Status id={value ? "active" : "inactive"} withIcon />;
        },
    },
];

const Clients = ({ form, visible, onBackClick, onClientSelected, onSearchByChange }) => {
    const dispatch = useDispatch();
    const [filterTemp, setFilterTemp] = useState("");
    const [filter, setFilter] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pageableData, setPageableData] = useState(undefined);
    const { Customers } = useContext(FormsContext);
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });

    function getCustomers(requestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        Customers.getList({
            onSuccess: (data, pageableData) => {
                setData(data);
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                dispatch(notifyInfo(info));
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    useEffect(() => {
        setFilterTemp("");
        setFilter("");
        setData([]);

        return () => {
            setData([]);
        };
    }, [visible, form]);

    function filterBuilder(value = filter) {
        return value
            ? {
                  firstName: value,
                  lastName: value,
                  middleName: value,
                  email: value,
              }
            : {};
    }

    const onFilterChange = (e) => {
        setFilterTemp(e.target.value);
    };

    const onSearchClick = () => {
        const value = filterTemp.trim();

        if (!value || !form || !visible) {
            return;
        }

        setFilter(value);
        getCustomers({
            filter: filterBuilder(value),
            orderBy: "firstName",
            orderDirection: "asc",
            pageNumber: 0,
            pageSize: 5,
        });
    };

    const onFilterKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            onSearchClick();
        }
    };

    const onRowClickHandler = (item) => {
        if (item.enabled) {
            onClientSelected(item);
        } else {
            setConfirmDialogProps((prev) => ({
                ...prev,
                open: true,
                title: "Client disabled",
                content: `${item.fullName} is a disabled client. Do you want to continue anyway?`,
                primaryButtonText: "Yes, continue",
                onPrimaryClick: () => {
                    onClientSelected(item);
                    prev.onClose();
                },
            }));
        }
    };

    return (
        <div
            className={clsx("layout clients-container", {
                visible: visible,
                invisible: !visible,
            })}
        >
            {visible && (
                <>
                    <Button secondary link onClick={onBackClick}>
                        <Icon name="arrow" color="secondary" size="xs" />
                        <span>Back</span>
                    </Button>
                    <hr />
                    <div className="step-title">
                        <h3>Client</h3>
                        <SearchByToggle value={SearchBy.Client} onChange={onSearchByChange} />
                    </div>
                    <small>
                        <i>Select the client</i>
                    </small>

                    <div className="quick-filter">
                        {isLoading && <Icon name="loading" color="secondary" size="sm" />}
                        {!isLoading && <Icon name="search" color="secondary" size="sm" />}
                        <TextField
                            type="text"
                            placeholder="Type known client's information here..."
                            value={filterTemp}
                            onChange={onFilterChange}
                            onKeyDown={onFilterKeyDown}
                            disabled={isLoading}
                            fullWidth
                        />
                        <Button primary small onClick={onSearchClick} disabled={isLoading || !filterTemp.trim()}>
                            Search
                        </Button>
                    </div>

                    <div className="clients">
                        {isLoading && <LoadingPage />}
                        {!isLoading && hasElements(data) && (
                            <EnhancedTable
                                headCells={headCells}
                                data={data}
                                dense={false}
                                defaultOrder={{ order: "asc", orderBy: "firstName" }}
                                keyColumnName="id"
                                selectable={false}
                                deletable={false}
                                filterable={false}
                                onRowClick={onRowClickHandler}
                                onRequestData={getCustomers}
                                filterBuilder={filterBuilder}
                                pageableData={pageableData}
                            />
                        )}
                    </div>

                    <ConfirmDialog {...confirmDialogProps} />
                </>
            )}
        </div>
    );
};

Clients.propTypes = {
    form: PropTypes.object,
    visible: PropTypes.bool,
    onBackClick: PropTypes.func,
    onClientSelected: PropTypes.func,
    onSearchByChange: PropTypes.func,
};

Clients.defaultProps = {
    form: {},
    visible: false,
    onBackClick: () => {},
    onClientSelected: () => {},
    onSearchByChange: () => {},
};

export default Clients;
